/**
 * Wall-clock time-of-day helpers for the event form.
 *
 * These deal ONLY with a "HH:MM" 24-hour string as typed by an organiser. They
 * know nothing about timezones or instants — that is `@/lib/eventTime`'s job,
 * and the two must not be confused. The form collects a wall-clock time; the
 * backend anchors it to the event's zone.
 *
 * Extracted from MeetingDraftForm so the meridiem rules can be tested directly.
 * The bug they exist to prevent: the end-time AM/PM toggle defaulted to AM and
 * nothing ever carried the start's meridiem across, so a 2:00 PM start with an
 * end hour of "3" silently stored 03:00 — and an auto-correct then nudged it to
 * 03:01 rather than admitting the range was invalid.
 */

export type Meridiem = "AM" | "PM";

/** Minutes since midnight, or null if `time` is not a "HH:MM" string. */
export function timeToMinutes(time: string | null | undefined): number | null {
    if (!time || !time.includes(":")) return null;
    const [hourStr, minuteStr] = time.split(":");
    const hour = Number(hourStr);
    const minute = Number(minuteStr);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
    return hour * 60 + minute;
}

/** Split a 24h "HH:MM" into its 12-hour parts. Returns `ampm: null` for an
 *  empty or malformed value — deliberately NOT "AM", which is what made the end
 *  field look pre-answered. A caller that needs a fallback must choose one. */
export function to12h(time24: string | null | undefined): {
    h: string;
    m: string;
    ampm: Meridiem | null;
} {
    if (!time24 || !time24.includes(":")) return { h: "", m: "", ampm: null };
    const [hourStr, minStr] = time24.split(":");
    const h24 = Number(hourStr);
    if (Number.isNaN(h24)) return { h: "", m: "", ampm: null };
    const ampm: Meridiem = h24 >= 12 ? "PM" : "AM";
    const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
    return { h: String(h12), m: minStr.padStart(2, "0"), ampm };
}

/** Build a 24h "HH:MM" from 12-hour parts. */
export function from12h(h: string, m: string, ampm: Meridiem): string {
    if (!h || !m) return "";
    let hour = Number(h);
    if (Number.isNaN(hour)) return "";
    if (ampm === "AM" && hour === 12) hour = 0;
    else if (ampm === "PM" && hour !== 12) hour += 12;
    return `${String(hour).padStart(2, "0")}:${m.padStart(2, "0")}`;
}

/**
 * The meridiem to show for a field, in priority order: the one implied by the
 * value itself, else the one inherited from the start time, else none.
 *
 * This is the single source of truth the three independent "AM" defaults used
 * to fight over.
 */
export function displayMeridiem(
    value: string | null | undefined,
    inherited: Meridiem | null = null,
): Meridiem | null {
    return to12h(value).ampm ?? inherited;
}

/** True when `end` is strictly after `start`. An absent value is not an error
 *  here — the submit guard is what insists both are present. */
export function isEndTimeAfterStart(
    start: string | null | undefined,
    end: string | null | undefined,
): boolean {
    const startMinutes = timeToMinutes(start);
    const endMinutes = timeToMinutes(end);
    if (startMinutes === null || endMinutes === null) return true;
    return endMinutes > startMinutes;
}

/** "1h 30m", "45m", "2h" — the duration between two wall-clock times, or null.
 *  Shown next to the end field because an organiser read the end time AS a
 *  duration ("I thought it's how many hours?"); stating both removes the guess. */
export function durationLabel(
    start: string | null | undefined,
    end: string | null | undefined,
): string | null {
    const startMinutes = timeToMinutes(start);
    const endMinutes = timeToMinutes(end);
    if (startMinutes === null || endMinutes === null) return null;
    const total = endMinutes - startMinutes;
    if (total <= 0) return null;
    const hours = Math.floor(total / 60);
    const minutes = total % 60;
    if (hours && minutes) return `${hours}h ${minutes}m`;
    if (hours) return `${hours}h`;
    return `${minutes}m`;
}

/** `start` plus `minutes`, as "HH:MM", or null if it would run past midnight.
 *  Null rather than wrapping: the form stores one date for both ends, so a
 *  wrapped value would silently become an event that ends before it starts. */
export function addMinutes(
    start: string | null | undefined,
    minutes: number,
): string | null {
    const startMinutes = timeToMinutes(start);
    if (startMinutes === null) return null;
    const total = startMinutes + minutes;
    if (total >= 24 * 60) return null;
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/** "5:30 PM" for a 24h string — for labels and summaries, never for an event
 *  instant (use `@/lib/eventTime` for those). */
export function formatTimeOfDay(time24: string | null | undefined): string {
    const { h, m, ampm } = to12h(time24);
    return h && ampm ? `${h}:${m} ${ampm}` : "";
}

/** "1h 30m", "45m", "2h" for a length in minutes, or "" for a non-positive one. */
export function formatDuration(minutes: number | null | undefined): string {
    if (!minutes || minutes <= 0) return "";
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (hours && rest) return `${hours}h ${rest}m`;
    if (hours) return `${hours}h`;
    return `${rest}m`;
}

/** The event lengths offered: 30 minutes to 8 hours in 30-minute steps. The
 *  form asks for a duration rather than an end time because organisers read
 *  the end field AS a duration ("I thought it's how many hours?"). */
export const DURATION_STEP_MINUTES = 30;
export const DURATION_OPTIONS: number[] = Array.from(
    { length: (8 * 60) / DURATION_STEP_MINUTES },
    (_, i) => (i + 1) * DURATION_STEP_MINUTES,
);

/** Duration options, always including `current` so an existing event whose
 *  length is off the 30-minute grid rehydrates without losing its value. */
export function durationOptions(current?: number | null): number[] {
    if (current && current > 0 && !DURATION_OPTIONS.includes(current)) {
        return [...DURATION_OPTIONS, current].sort((a, b) => a - b);
    }
    return DURATION_OPTIONS;
}

/** Minutes between two wall-clock date+time pairs ("YYYY-MM-DD", "HH:MM"), or
 *  null if either is missing or the end is not after the start. Date-aware, so
 *  an event that runs past midnight still yields its real length. */
export function minutesBetween(
    startDate: string | null | undefined,
    startTime: string | null | undefined,
    endDate: string | null | undefined,
    endTime: string | null | undefined,
): number | null {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    if (!startDate || !endDate || startMinutes === null || endMinutes === null) return null;
    const dayMs = 24 * 60 * 60 * 1000;
    const startDay = Date.parse(`${startDate}T00:00:00Z`);
    const endDay = Date.parse(`${endDate}T00:00:00Z`);
    if (Number.isNaN(startDay) || Number.isNaN(endDay)) return null;
    const total = Math.round((endDay - startDay) / dayMs) * 24 * 60 + endMinutes - startMinutes;
    return total > 0 ? total : null;
}

/** `date` + `time` plus `minutes`, rolling the date forward past midnight —
 *  unlike `addMinutes`, which refuses to. Pure calendar arithmetic in UTC so no
 *  browser-zone DST shift can leak into what is a wall-clock value. */
export function addMinutesToDateTime(
    date: string | null | undefined,
    time: string | null | undefined,
    minutes: number,
): { date: string; time: string } | null {
    const startMinutes = timeToMinutes(time);
    if (!date || startMinutes === null) return null;
    const base = Date.parse(`${date}T00:00:00Z`);
    if (Number.isNaN(base)) return null;
    const end = new Date(base + (startMinutes + minutes) * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, "0");
    return {
        date: `${end.getUTCFullYear()}-${pad(end.getUTCMonth() + 1)}-${pad(end.getUTCDate())}`,
        time: `${pad(end.getUTCHours())}:${pad(end.getUTCMinutes())}`,
    };
}

/** Minute options, in 5-minute steps, always including `current` so an existing
 *  event on an odd minute rehydrates without losing its value. */
export function minuteOptions(current?: string | null): string[] {
    const steps = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
    if (current && !steps.includes(current)) {
        return [...steps, current].sort();
    }
    return steps;
}

export const HOUR_OPTIONS: string[] = Array.from({ length: 12 }, (_, i) => String(i + 1));
